import { useMutation } from "@tanstack/react-query";
import HttpService from "@/hooks/Https-services";
import useConfig from "@/hooks/useConfig";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";
import { notifyAuthChanged } from "@/v2/hooks/useAuthState";

export type UserProfileData = {
  UserID: string | number;
  FirstName: string;
  LastName: string;
  Email: string;
  ProfileImage?: string;
  createdAt?: string;
  IsEmailVerified?: boolean;
};

/**
 * Keep the localStorage `auth` object (which the v2 header reads via
 * useAuthState) in sync after a profile mutation, using whatever fields the
 * server echoed back.
 */
function syncAuthFromResponse(data: any) {
  const auth = getCurrentAuth() as any;
  if (!auth || !data) return;
  localStorage.setItem(
    "auth",
    JSON.stringify({
      ...auth,
      FirstName: data.first_name ?? data.FirstName ?? auth.FirstName,
      LastName: data.last_name ?? data.LastName ?? auth.LastName,
      Email: data.email ?? data.Email ?? auth.Email,
      ProfileImage: data.image ?? data.ProfileImage ?? auth.ProfileImage,
      IsEmailVerified: data.IsEmailVerified ?? data.is_email_verified ?? data.isEmailVerified ?? auth.IsEmailVerified,
    })
  );
  notifyAuthChanged({ profileOnly: true });
}

/** Mutation to update the display name. */
export function useUpdateProfile() {
  return useMutation({
    mutationFn: async (data: { first_name: string; last_name: string }) => {
      const res = await HttpService.post(APIRoutes.EditProfile, data);
      return res.data;
    },
    onSuccess: (resData) => syncAuthFromResponse(resData?.data),
  });
}

/** Mutation to upload and set the profile photo. */
export function useUpdatePhoto() {
  const config = useConfig();
  const s3HostUrl = config?.s3HostUrl || "";

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await HttpService.postImage(
        APIRoutes.UploadImage,
        formData
      );
      const imagePath = uploadRes.data?.data;
      if (!imagePath) throw new Error("Upload failed");

      const updateRes = await HttpService.post(APIRoutes.EditProfile, {
        image: `${s3HostUrl}${imagePath}`,
      });
      return updateRes.data;
    },
    onSuccess: (resData) => syncAuthFromResponse(resData?.data),
  });
}

/** Mutation to change the password. */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: Record<string, string>) => {
      const res = await HttpService.post(APIRoutes.ChangePassword, data);
      return res.data;
    },
  });
}
